import { useCallback, useState } from 'react';

import { Typography } from '@mui/material';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { DeleteDialog } from 'common/components/deleteDialog/DeleteDialog';
import {
    selectCurrentEntityType,
    setCurrentEntityType,
    setDialogOpen
} from 'common/components/deleteDialog/deleteDialogSlice';
import { extractStatusCode } from 'common/services/api/apiHelpers';
import { fetchIdentitiesById } from 'common/services/api/Identities/Identities.thunk';
import { deleteUserIdentity } from 'common/services/api/Users/Users.thunk';
import { IdentityOemMapping } from 'common/types/Entities';
import { DeleteDialogContentProps, DrawerEntitiesNames } from 'common/types/TypeDefs';
import { DEFAULT_ERROR_MESSAGE } from 'common/utils/errorHandlers';
import { setUserDetails } from 'features/users/usersSlice';
import { useSnackbar } from 'notistack';

export function UserProfileIdentitiesDeleteDialog({ editingRow }: Readonly<DeleteDialogContentProps<IdentityOemMapping | null>>) {
    const { oemId, oemIdPName, subject, authority, email } = editingRow ?? {};
    const { enqueueSnackbar } = useSnackbar();
    const [processing, setProcessing] = useState<boolean>(false);
    const dispatch = useAppDispatch();
    const currentEntityType = useAppSelector(selectCurrentEntityType);

    const userIdentityDeleteCallback = useCallback(() => {
        if (!oemId) {
            return;
        }

        setProcessing(true);
        dispatch(deleteUserIdentity({
            userId: oemId,
            issuer: authority ?? '',
            subject: subject ?? '',
        }))
            .unwrap()
            .then((result) => {
                dispatch(setUserDetails(result));
                dispatch(setDialogOpen(false));
                dispatch(setCurrentEntityType());
                enqueueSnackbar('The Identity has been removed from this User.', { variant: 'success' });
                dispatch(fetchIdentitiesById({ userId: oemId }));
            })
            .catch((error) => {
                if (extractStatusCode(error.message) === 422) {
                    enqueueSnackbar('Users are required to have at least one Identity, therefore, this Identity cannot be deleted.', { variant: 'error' });
                } else {
                    enqueueSnackbar(
                        'There was an error while removing this Identity from this User.' +
                        DEFAULT_ERROR_MESSAGE,
                        { variant: 'error' }
                    );
                }
            })
            .finally(()=> {
                setProcessing(false);
            });
    }, [oemId]);

    if (currentEntityType !== DrawerEntitiesNames.UserIdentities) {
        return null;
    }

    return  (
        <DeleteDialog deleteCallback={userIdentityDeleteCallback} processing={processing ?? false} title={'Identity'}>
            <Typography color="info.light" fontSize='0.9rem' fontWeight='550'>
                Identity Provider Name: <Typography component="span" color="text.primary" fontWeight="400">{oemIdPName}</Typography>
            </Typography>
            {email && <Typography color="info.light" fontSize='0.9rem' fontWeight='550'>
                Email Address: <Typography component="span" color="text.primary" fontWeight="400">{email}</Typography>
            </Typography>}
            {subject && <Typography color="info.light" fontSize='0.9rem' fontWeight='550'>
                User Key: <Typography component="span" color="text.primary" fontWeight="400">{subject}</Typography>
            </Typography>}
        </DeleteDialog>
    );
}
